import React from "react";
import { useSelector } from "react-redux";
import useStudentsPageQueries from "../../store/students/studentQueries";
import { getAccountType } from "../../store/auth/selectors";
import { CONFIG_VARS } from "../../constants/urlConstants/configuration";
import {
  checkPermission,
  PERMISSION_TYPES,
} from "../../shared/utils/permissions";
import EventsSection from "../../shared/components/EventsSection/EventsSection";
import UploadButton from "../../shared/components/UploadButton/UploadButton";
import StudentTable from "../../shared/components/Table/StudentTable";
import Pagination from "../../shared/components/Pagination/Pagination";

const isWithinNext7Days = (dateString) => {
  if (!dateString) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventDate = new Date(dateString);
  eventDate.setHours(0, 0, 0, 0);

  const sevenDaysFromEvent = new Date(eventDate);
  sevenDaysFromEvent.setDate(eventDate.getDate() + 7);

  return today <= sevenDaysFromEvent;
};

const generateDeeplink = (student, selectedEvent) => {
  const currentEnv = CONFIG_VARS.currentEnvironment;
  const baseUrl = CONFIG_VARS.deeplinkBaseUrl[currentEnv];
  const isProd = currentEnv === "prod";

  let examStatus = "missed";
  if (student.attempt === "Yes") {
    examStatus = "completed";
  } else if (
    selectedEvent?.start_date_time &&
    isWithinNext7Days(selectedEvent.start_date_time)
  ) {
    examStatus = "scheduled";
  }

  if (isProd) {
    const queryParams = new URLSearchParams({
      exam_status: examStatus,
      crnId: student.crn_id || "",
      eventId: student.event_id || "",
      id: student.loginId || "",
      password: "qwerty",
    });

    return `${baseUrl}?${queryParams.toString()}`;
  }

  return baseUrl;
};

const EventsTabContent = ({
  events,
  onEventClick,
  eventLoader,
  selectedEvent,
  error,
  isLoading,
  eventStudentsTotal,
  eventAttemptedTotal,
  eventCurrentPage,
  eventItemsPerPage,
  onEventPageChange,
  onEventItemsPerPageChange,
  paginatedStudents,
  columns,
  selectedStudents,
  onSelectAll,
  onSelectRow,
  schoolId,
  attemptFilter,
  onAttemptFilterChange,
}) => {
  const accountType = useSelector(getAccountType);
  const canUploadToEvent = useSelector(
    (state) => state.uploadPermissions.canUploadToEvent
  );
  const checkUserPermission = (permissionType) =>
    checkPermission(accountType, permissionType);

  const canUploadStudents =
    checkUserPermission(PERMISSION_TYPES.UPLOAD_STUDENTS) && canUploadToEvent;
  const canExportData = checkUserPermission(
    PERMISSION_TYPES.EXPORT_STUDENT_DATA
  );
  const canUploadResponse = checkUserPermission(
    PERMISSION_TYPES.UPLOAD_RESPONSE
  );

  const { handleExportStudentsByEvent } = useStudentsPageQueries();

  const handleExportCSV = async () => {
    if (!selectedEvent?.scheduled_events) return;

    try {
      const response = await handleExportStudentsByEvent(
        selectedEvent.scheduled_events,
        attemptFilter
      );

      if (response?.export_url) {
        const link = document.createElement("a");
        link.href = response.export_url;
        link.setAttribute(
          "download",
          `students_export_${
            selectedEvent.event || "data"
          }_${attemptFilter}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error exporting students data:", error);
    }
  };

  const totalAttempted = eventAttemptedTotal || 0;

  const studentsWithDeeplinks = React.useMemo(() => {
    return (
      paginatedStudents?.map((student) => ({
        ...student,
        deepLink: generateDeeplink(student, selectedEvent),
      })) || []
    );
  }, [paginatedStudents, selectedEvent]);

  return (
    <div className="events-tab-content">
      <div className="events-tab-header">
        <EventsSection
          events={events}
          onEventClick={onEventClick}
          eventLoader={eventLoader}
          activeEvent={selectedEvent}
        />
        {schoolId && canUploadStudents && (
          <UploadButton
            schoolId={schoolId}
            selectedEvent={selectedEvent}
            uploadType={"studentRegister"}
            selectedTab="Events"
          />
        )}
        {schoolId &&
          canUploadResponse &&
          selectedEvent?.event_type === "OFFLINE" && (
            <UploadButton
              schoolId={schoolId}
              selectedEvent={selectedEvent}
              uploadType={"uploadOfflineResponses"}
              selectedTab="Events"
            />
          )}
      </div>
      {error && <div className="error-message">{error}</div>}
      {isLoading && <div className="loading-message">Loading events...</div>}
      {selectedEvent ? (
        <>
          <div className="students-info">
            <span style={{ fontWeight: "500" }}>
              Total Students: {eventStudentsTotal} | Total Attempted:{" "}
              {totalAttempted}
            </span>
            <div className="table-controls">
              {canExportData && selectedEvent?.scheduled_events && (
                <button onClick={handleExportCSV} className="export-btn">
                  Export CSV
                </button>
              )}
              <select
                className="operation-select bulk-operations"
                value={attemptFilter}
                onChange={(e) => onAttemptFilterChange(e.target.value)}
              >
                <option value="ALL">All</option>
                <option value="ATTEMPTED">Attempted</option>
                <option value="NON_ATTEMPTED">Non-Attempted</option>
              </select>
              <Pagination
                totalItems={
                  attemptFilter === "ATTEMPTED"
                    ? eventAttemptedTotal
                    : attemptFilter === "NON_ATTEMPTED"
                    ? eventStudentsTotal - eventAttemptedTotal
                    : eventStudentsTotal
                }
                currentPage={eventCurrentPage}
                itemsPerPage={eventItemsPerPage}
                itemsPerPageOptions={[100, 200, 500, 1000, 2000]}
                onPageChange={onEventPageChange}
                onItemsPerPageChange={onEventItemsPerPageChange}
              />
            </div>
          </div>
          {studentsWithDeeplinks.length > 0 ? (
            <StudentTable
              data={studentsWithDeeplinks}
              columns={columns}
              selectedRows={selectedStudents}
              onSelectAll={onSelectAll}
              onSelectRow={onSelectRow}
            />
          ) : (
            <div className="error-message">No students found</div>
          )}
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

export default EventsTabContent;
